import { Badge, Skeleton } from "@/components/kit";
import type { CustomerRiskDTO } from "@/modules/customers/domain/types";
import { formatLak } from "@/shared/lib/currency";
import {
  customerRiskTierMeta,
  formatDepositHint,
} from "./customer-risk";

type CustomerRiskCardProps = {
  risk?: CustomerRiskDTO;
  isLoading?: boolean;
};

export function CustomerRiskCard({ risk, isLoading }: CustomerRiskCardProps) {
  if (isLoading) {
    return <Skeleton className="h-40 w-full rounded-2xl" />;
  }
  if (!risk) return null;

  const meta = customerRiskTierMeta(risk.tier);
  const depositHint = formatDepositHint(risk);
  const f = risk.features;

  return (
    <section className="brand-card flex flex-col gap-4 p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex flex-col gap-1">
          <h3 className="font-display text-lg">ຄວາມສ່ຽງ COD</h3>
          <p className="text-muted-foreground text-sm">{meta.actionLabel}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant={meta.variant} className="rounded-full">
            {meta.label}
          </Badge>
          <span className="text-muted-foreground text-sm tabular-nums">
            ຄະແນນ {risk.score}/100
          </span>
        </div>
      </div>

      {depositHint ? (
        <p className="rounded-lg bg-muted/40 px-3 py-2 text-sm">{depositHint}</p>
      ) : null}

      <ul className="flex flex-col gap-1.5 text-sm">
        {risk.reasons.map((reason) => (
          <li key={reason} className="text-muted-foreground">
            · {reason}
          </li>
        ))}
      </ul>

      <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
        <FeatureStat
          label="COD ສຳເລັດ"
          value={
            f.codOrderCount > 0
              ? `${f.codPickedUpCount}/${f.codOrderCount}`
              : "—"
          }
        />
        <FeatureStat
          label="ອັດຕາຕີກັບ"
          value={
            f.returnRate != null
              ? `${(f.returnRate * 100).toFixed(0)}%`
              : "—"
          }
        />
        <FeatureStat
          label="ສາຂາຕີກັບ"
          value={
            f.branchReturnRate != null && f.branchShippedCount > 0
              ? `${(f.branchReturnRate * 100).toFixed(0)}%${
                  f.lastShippingBranch ? ` · ${f.lastShippingBranch}` : ""
                }`
              : "—"
          }
        />
        <FeatureStat
          label="ຍອດສະເລ່ຍ"
          value={f.avgOrderValue ? formatLak(f.avgOrderValue) : "—"}
        />
        <FeatureStat
          label="ລໍຖ້າຮັບ (ວັນ)"
          value={
            f.avgPickupDays != null ? String(f.avgPickupDays) : "—"
          }
        />
      </div>
    </section>
  );
}

function FeatureStat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg bg-muted/30 px-3 py-2">
      <p className="text-muted-foreground text-xs">{label}</p>
      <p className="font-medium text-sm tabular-nums">{value}</p>
    </div>
  );
}
